import moment from "moment";

import { declareAiTabDestination } from "@medflyt/webapp-react/src/modules/ai-omnibar/ai-tab-destinations";
import {
    declareAiCapability,
    provideAiCapability,
} from "@medflyt/webapp-react/src/modules/ai-omnibar/ai-capability-registry";

// The tab saying it is somewhere to go. Care & Task management opens on Tasks, so this tab is only
// reachable by name if it says so itself.
declareAiTabDestination({
    id: "certificationPeriods.open",
    title: "Open the Certification Periods tab",
    stateName: "app.patients.careAndTaskManagement.certificationPeriods",
    opens: "page:certificationPeriods",
    description:
        "Opens Patients > Care & Task management > Certification Periods: each patient's certification period, when it started and ends, and how many days are left on it.",
    location: "the tab bar of Care & Task management",
    permissionKey: "view_patients_section_upcoming_tasks",
    examples: ["open certification periods", "certifications expiring in the next 30 days"]
});

// Certification periods describing its own filter bar. The dropdown options are built from the rows
// on screen rather than a fixed list, so they are read when the manifest is built - a value that is
// not in the data is not a value this page can filter by, and saying so beats offering it.
declareAiCapability({
    id: "certificationPeriods.filter",
    title: "Filter the certification periods list",
    effect: "prefill",
    description:
        "Sets the filters on Patients > Care & Task management > Certification Periods: certification status, the patient's status, office, zip code, how many days are left on the certification, and date ranges for start of care, certification start and certification end. Also takes a free-text search over the table. Anything left out keeps what the page already has. Only works while that tab is open.",
    location: "the filter bar of the Certification Periods tab",
    scope: "page:certificationPeriods",
    permissionKey: "view_patients_section_upcoming_tasks",
    params: [
        {
            name: "status",
            type: "enum",
            required: false,
            multiple: true,
            options: () => getCertificationPeriodDropdownOptions("Status"),
            description: "Limit to certifications in this state, e.g. not completed."
        },
        {
            name: "patientStatus",
            type: "enum",
            required: false,
            multiple: true,
            options: () => getCertificationPeriodDropdownOptions("Patient Status"),
            description: "Limit to patients in this status."
        },
        {
            name: "office",
            type: "enum",
            required: false,
            multiple: true,
            options: () => getCertificationPeriodDropdownOptions("Office"),
            description: "Limit to patients in this office."
        },
        {
            name: "zipCode",
            type: "enum",
            required: false,
            multiple: true,
            options: () => getCertificationPeriodDropdownOptions("Zip Code"),
            description: "Limit to patients in this zip code."
        },
        {
            name: "daysLeft",
            type: "number",
            required: false,
            description:
                "Keep only certifications with this many days left or fewer. Use it for \"expiring in the next 30 days\" and the like."
        },
        {
            name: "certificationEndFrom",
            type: "date",
            required: false,
            description: "Earliest certification end date to include, as yyyy-MM-dd."
        },
        {
            name: "certificationEndTo",
            type: "date",
            required: false,
            description: "Latest certification end date to include, as yyyy-MM-dd."
        },
        {
            name: "certificationStartFrom",
            type: "date",
            required: false,
            description: "Earliest certification start date to include, as yyyy-MM-dd."
        },
        {
            name: "certificationStartTo",
            type: "date",
            required: false,
            description: "Latest certification start date to include, as yyyy-MM-dd."
        },
        {
            name: "startOfCareFrom",
            type: "date",
            required: false,
            description: "Earliest patient start-of-care date to include, as yyyy-MM-dd."
        },
        {
            name: "startOfCareTo",
            type: "date",
            required: false,
            description: "Latest patient start-of-care date to include, as yyyy-MM-dd."
        },
        {
            name: "search",
            type: "text",
            required: false,
            searchesFreeText: true,
            description:
                "Free text the table searches over every column. Use it for a patient name or word that is not one of the filters above."
        }
    ],
    examples: [
        "certification periods expiring in the next 30 days",
        "not completed certifications in the europe office",
        "certification periods ending this year for active patients"
    ]
});

/**
 * The options one of this page's dropdowns currently has, in the shape the omnibar reads.
 *
 * Read live, not captured: the lists are built from the rows the page has loaded, so they are empty
 * until the fetch lands and they shrink when the data does.
 */
let certificationPeriodDropdowns = () => [];

function getCertificationPeriodDropdownOptions(title) {
    const dropdown = certificationPeriodDropdowns().find((one) => one.title === title);

    return (dropdown?.options ?? []).map((option) => ({
        value: String(option.id),
        label: String(option.label ?? option.id)
    }));
}

//! @ngInject
export function certificationPeriodsCtrl(
    $scope,
    $rootScope,
    $q,
    DatabaseApi,
    toaster,
    NgTableParams,
    $filter,
    mfModal,
    createDropdownFilter,
    agencyPatientStatusService,
    officesService,
    createTaskModalService,
    patientLookupService
) {
    let dropdownFilterManager;
    let officeOptions = [];
    let officeNameById = new Map();

    const getOfficeDropdown = () =>
        $scope.filterBy?.dropdowns?.find((dropdown) => dropdown.title === "Office");

    const updateOfficeDropdownOptions = () => {
        const dropdown = getOfficeDropdown();
        if (!dropdown) {
            return;
        }

        if (!Array.isArray($scope.certificationPeriods) || $scope.certificationPeriods.length === 0) {
            dropdown.options = [...officeOptions];
            return;
        }

        const officeIdsInData = new Set(
            $scope.certificationPeriods
                .map((row) => row?.patient?.currentOfficeId)
                .filter((officeId) => officeId !== null && officeId !== undefined)
        );

        dropdown.options = officeOptions.filter((option) => officeIdsInData.has(option.id));
    };

    const updateOfficeNames = () => {
        if (!Array.isArray($scope.certificationPeriods) || $scope.certificationPeriods.length === 0) {
            return;
        }

        $scope.certificationPeriods.forEach((row) => {
            if (!row.patient) {
                return;
            }
            const officeId = row.patient.currentOfficeId;
            row.patient.officeName = officeId ? officeNameById.get(officeId) : undefined;
        });
    };

    $scope.dropdownFilterEvents = {
        onSelectionChanged: () => $scope.applyFilters(),
    };

    const initialize = () => {
        $scope.certificationPeriodsGlobalFilter = { val: "" };
        $scope.filterBy = {
            daysLeft: undefined,
            startOfCareDate: { startDate: null, endDate: null },
            certificationStartDate: { startDate: null, endDate: null },
            certificationEndDate: { startDate: null, endDate: null },
            dropdowns: [
                {
                    title: "Status",
                    entityPath: ["status", "label"],
                    options: [],
                    values: [
                        { id: "Not Completed", label: "Not Completed" }
                    ]
                },
                {
                    title: "Patient Status",
                    entityPath: ["patient", "status", "label"],
                    options: [],
                    values: [],
                },
                {
                    title: "Zip Code",
                    entityPath: ["patient", "zipCode"],
                    options: [],
                    values: [],
                    settings: {
                        enableSearch: true,
                        scrollable: true,
                        scrollableHeight: 400
                    }
                },
                {
                    title: "Office",
                    entityPath: ["patient", "currentOfficeId"],
                    options: [],
                    values: [],
                    settings: {
                        enableSearch: true,
                        scrollable: true,
                        scrollableHeight: 400
                    }
                },
            ],
        };

        dropdownFilterManager = createDropdownFilter({
            dropdowns: $scope.filterBy.dropdowns
        });

        $scope.dateRangeOptions = {
            ranges: {
                "Last 7 Days": [moment().subtract(6, "days"), moment()],
                "Last 14 Days": [moment().subtract(13, "days"), moment()],
                "Last 30 Days": [moment().subtract(29, "days"), moment()],
                "Last Year": [
                    moment().subtract(1, "year").startOf("year"),
                    moment().subtract(1, "year").endOf("year"),
                ],
                "This Year": [moment().startOf("year"), moment().endOf("year")],
                "Last Month": [
                    moment().subtract(1, "month").startOf("month"),
                    moment().subtract(1, "month").endOf("month"),
                ],
                "This Month": [moment().startOf("month"), moment().endOf("month")],
            },
            alwaysShowCalendars: true,
            applyClass: "btn-primary",
            locale: {
                direction: "ltr date-range-picker-v2",
                format: "D MMM YY"
            },
            opens: "left",
            autoApply: true,
            minDate: new Date("2001-01-01"),
            eventHandlers: {
                "apply.daterangepicker": $scope.applyFilters
            },
        };

        officesService
            .getOffices({ onlyActives: true, refetch: false })
            .then((offices) => {
                officeOptions = offices.map((office) => ({
                    id: office.id,
                    label: office.name
                }));
                $scope.officeNameById = new Map(offices.map((office) => [office.id, office.name]));
                updateOfficeDropdownOptions();
            });

        fetchData();
    };

    const fetchData = () => {
        const url = "agencies/:agencyId/agency_members/:agencyMemberId/certification_periods"
            .replace(":agencyId", $rootScope.agencyId)
            .replace(":agencyMemberId", $rootScope.agencyMemberId);
        DatabaseApi.get(url).then((res) => {
            $scope.certificationPeriods = res.data.certificationPeriods;
            const patientIds = Array.from(new Set($scope.certificationPeriods.map(row => row.patientId)));
            // The patients arrive with the periods. Only the structured address -
            // a different projection - still needs its own request, and that
            // endpoint has always taken ids.
            const patients = new Map(
                (res.data.patients ?? []).map((patient) => [String(patient.id), patient])
            );
            patientLookupService.addressesByIds(patientIds)
                .then((addresses) => {
                    $scope.certificationPeriods.forEach(row => {
                        const patient = patients.get(String(row.patientId));
                        if (patient === undefined) {
                            return;
                        }
                        const address = addresses.get(String(row.patientId));
                        const currentOfficeId = patient.currentOfficeId;
                        row.patient = {
                            displayName: patient.displayName,
                            status: {
                                label: $filter("capitalize")(patient.status),
                                class: agencyPatientStatusService.getStatusClass(patient.status)
                            },
                            zipCode: address?.components?.formatedAddressDetails?.zip5,
                            addressText: address?.text,
                            currentOfficeId: currentOfficeId,
                            officeName: currentOfficeId ? $scope.officeNameById.get(currentOfficeId) : undefined
                        };
                        const endDateMoment = moment(row.endDate);
                        const nowMoment = moment().set({hour: 0, minute: 0, second: 0, millisecond: 0});
                        setRowStatus(row);
                        if (!nowMoment.isAfter(endDateMoment)) {
                            row.daysLeft = endDateMoment.diff(nowMoment, "days");
                        } else {
                            row.daysLeft = 0 - nowMoment.diff(endDateMoment, "days");
                        }
        
                        if (row.isCompleted) {
                            row.daysLeft = null;
                        }
                    });
                    dropdownFilterManager.setOptionsFromLocalData($scope.certificationPeriods);
                    updateOfficeDropdownOptions();
                    $scope.applyFilters();
                });            
        })
        .catch((err) => {
            toaster.pop("error", "Oops... Something went wrong", "Failed to fetch certification periods");
        });
    };

    const setRowStatus = (row) => {
        row.status = {
            label: row.isCompleted ? "Completed" : "Not Completed",
            color: row.isCompleted ? "blue" : "grey",
        };
    };

    const initCertificationPeriodsTable = (certificationPeriods) => {
        const options = {
            count: 25
        };
        $scope.certificationPeriodsTable = new NgTableParams(options, {
            dataset: certificationPeriods,
        });
    };

    $scope.exportTable = () => {
        const tableData = $scope.certificationPeriodsTable.data;
        if (tableData.length <= 0) {
            toaster.pop('warning', 'Nothing to export', 'The table is empty');
            return;
        }

        $scope.loadingCSV = true;

        const rows = [];
        const titles = [
            'Patient Name',
            'Office Name',
            'Zip Code',
            'Address',
            'Patient Status',
            'Start of Care Date',
            'Certification Start Date',
            'Certification End Date',
            'Days Left',
        ];

        rows.push(titles);

        tableData.forEach(dataRow => {
          const csvRow = [];

          titles.forEach(function (title) {
            if (title === 'Patient Name')  + csvRow.push('"' + (dataRow.patient.displayName || '').replace(/(\r\n|\n|\r)/gm, "") + '"');
            else if (title === 'Office Name') + csvRow.push('"' + (dataRow.patient.officeName || '').replace(/(\r\n|\n|\r)/gm, "") + '"');
            else if (title === 'Zip Code') csvRow.push(dataRow.patient.zipCode);
            else if (title === 'Address') csvRow.push('"' + dataRow.patient.addressText + '"');
            else if (title === 'Patient Status') csvRow.push(dataRow.patient.status.label);
            else if (title === 'Start of Care Date') csvRow.push($filter("mfShortDate")(dataRow.patientStartOfCare));
            else if (title === 'Certification Start Date') csvRow.push($filter("mfShortDate")(dataRow.startDate));
            else if (title === 'Certification End Date') csvRow.push($filter("mfShortDate")(dataRow.endDate));
            else if (title === 'Days Left') csvRow.push(dataRow.daysLeft || '');
          });

          rows.push(csvRow);
        });

        let csvContent = "";
        rows.forEach(function (rowArray) {
            const row = rowArray.join(",");
            csvContent += row + "\r\n";
        });

        const universalBOM = "\uFEFF";
        const encodedUri = "data:text/csv;charset=utf-8," + encodeURIComponent(universalBOM + csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `medflyt-export-certification-periods-${$filter("date")(new Date(), "yyyy-MM-dd")}.csv`);
        document.body.appendChild(link);

        link.click();
        $scope.loadingCSV = false;
    };

    $scope.applyCertificationPeriodsGlobalSearch = (term) => {
        const filter = { $: term };
        if ($scope.certificationPeriodsTable) {
            angular.extend($scope.certificationPeriodsTable.filter(), filter);
        }
    };

    const filterByMethods = {
        startOfCareDate: (row, {startDate, endDate}) => moment(row.startOfCare).isBetween(startDate, endDate, undefined, "[]"),
        certificationStartDate: (row, {startDate, endDate}) => moment(row.startDate).isBetween(startDate, endDate, undefined, "[]"),
        certificationEndDate: (row, {startDate, endDate}) => moment(row.endDate).isBetween(startDate, endDate, undefined, "[]"),
        daysLeft: (row, daysLeft) => row.daysLeft && row.daysLeft <= daysLeft,
    };

    $scope.applyFilters = () => {
        let filters = [];

        if ($scope.filterBy.startOfCareDate && $scope.filterBy.startOfCareDate.startDate && $scope.filterBy.startOfCareDate.endDate) {
            filters.push((row) => filterByMethods.startOfCareDate(row, $scope.filterBy.startOfCareDate));
        }

        if ($scope.filterBy.certificationStartDate && $scope.filterBy.certificationStartDate.startDate && $scope.filterBy.certificationStartDate.endDate) {
            filters.push((row) => filterByMethods.certificationStartDate(row, $scope.filterBy.certificationStartDate));
        }

        if ($scope.filterBy.certificationEndDate && $scope.filterBy.certificationEndDate.startDate && $scope.filterBy.certificationEndDate.endDate) {
            filters.push((row) => filterByMethods.certificationEndDate(row, $scope.filterBy.certificationEndDate));
        }

        if (typeof $scope.filterBy.daysLeft === "number") {
            filters.push((row) => filterByMethods.daysLeft(row, $scope.filterBy.daysLeft));
        }

        filters.push((row) => dropdownFilterManager.filter(row));

        let filteredData = [];
        Object.assign(filteredData, $scope.certificationPeriods);
        if (filters.length > 0) {
            filteredData = filteredData.filter((row) => {
                let isFiltered = true;
                for (let idx = 0; isFiltered && idx < filters.length; idx++) {
                    isFiltered = isFiltered && filters[idx](row);
                }
                return isFiltered;
            });
        }

        initCertificationPeriodsTable(filteredData);
    };

    $scope.handleClearDaysLeftFilter = () => {
        $scope.filterBy.daysLeft = undefined;
        $scope.applyFilters();
    };

    $scope.resetFilterDateRange = (field) => {
        $scope.filterBy[field] = { startDate: null, endDate: null};
        $scope.applyFilters();
    };

    $scope.markAsCompleted = (certificationPeriod) => {
        const modal = mfModal.create({
            subject: "Mark as completed?",
            message: "This certification period will be marked as completed. Are you sure?",
            cancelLabel: "Cancel",
            confirmLabel: "Confirm",
            preventBackdropClose: true,
            onConfirm: () => {
                modal.setLoading(true);

                updateCertificationPeriod(certificationPeriod, { isCompleted: true }).then(() => {
                    certificationPeriod.isCompleted = true;
                    setRowStatus(certificationPeriod);
                    toaster.pop('success', 'Successfully marked as completed');
                    modal.close();
                }, () => {
                    toaster.pop('error', 'Failed to mark as completed');
                });
            },
        });
    };

    function updateCertificationPeriod(certificationPeriod, params) {
        const url =
          "agencies/:agencyId/agency_members/:agencyMemberId/patients/:patientId/certification_periods/:certificationPeriodId"
            .replace(":agencyId", $rootScope.agencyId)
            .replace(":agencyMemberId", $rootScope.agencyMemberId)
            .replace(":patientId", certificationPeriod.patientId)
            .replace(":certificationPeriodId", certificationPeriod.id);

        return DatabaseApi.put(url, {
            ...certificationPeriod,
            ...params
        });
    }

    $scope.openNewTaskModalByCertificationPeriod = (certificationPeriod) => {
        createTaskModalService
            .openNewTaskModalByCertificationPeriod(certificationPeriod)
            .then((res) => {
                if (res && res.taskInstanceId) {
                    certificationPeriod.hasTaskInstance = true;
                }
            });
    };

    // The dropdown lists live on the scope, so the declaration above reads them through this rather
    // than holding a copy that would go stale the moment the page refetched.
    certificationPeriodDropdowns = () => $scope.filterBy?.dropdowns ?? [];

    const releaseCertificationPeriodsFilter = provideAiCapability("certificationPeriods.filter", (args) => {
        const applied = [];
        const asList = (value) => (Array.isArray(value) ? value : [value]);

        const setDropdown = (title, requested, what) => {
            const dropdown = ($scope.filterBy?.dropdowns ?? []).find((one) => one.title === title);

            if (dropdown === undefined) {
                throw new Error("This page has no " + title + " filter.");
            }

            const wanted = asList(requested).map((one) => String(one).toLowerCase());
            const chosen = dropdown.options.filter(
                (option) =>
                    wanted.includes(String(option.id).toLowerCase()) ||
                    wanted.includes(String(option.label ?? "").toLowerCase())
            );

            if (chosen.length === 0) {
                throw new Error(
                    "Nothing on this page has " + what + " " + asList(requested).join(" or ") + "."
                );
            }

            dropdown.values = chosen;
            applied.push(chosen.map((option) => String(option.label ?? option.id)).join(" and "));
        };

        const asRange = (from, to) => {
            const parse = (text) => {
                const value = moment(String(text), "YYYY-MM-DD", true);

                if (!value.isValid()) {
                    throw new Error("Could not read the date " + text + ".");
                }

                return value;
            };

            // One end given is still a range to this page, which compares with isBetween - so the
            // other end becomes the far side of what it could mean rather than an empty filter.
            return {
                startDate: from === undefined ? moment("1900-01-01") : parse(from),
                endDate: to === undefined ? moment().add(50, "years") : parse(to)
            };
        };

        if (args.status !== undefined) {
            setDropdown("Status", args.status, "certification status");
        }

        if (args.patientStatus !== undefined) {
            setDropdown("Patient Status", args.patientStatus, "patient status");
        }

        if (args.office !== undefined) {
            setDropdown("Office", args.office, "office");
        }

        if (args.zipCode !== undefined) {
            setDropdown("Zip Code", args.zipCode, "zip code");
        }

        if (args.daysLeft !== undefined) {
            const days = Number(args.daysLeft);

            if (isNaN(days)) {
                throw new Error("Days left has to be a number, not " + args.daysLeft + ".");
            }

            $scope.filterBy.daysLeft = days;
            applied.push(days + " days left or fewer");
        }

        for (const [key, from, to, label] of [
            ["certificationEndDate", args.certificationEndFrom, args.certificationEndTo, "certification ending"],
            ["certificationStartDate", args.certificationStartFrom, args.certificationStartTo, "certification starting"],
            ["startOfCareDate", args.startOfCareFrom, args.startOfCareTo, "start of care"]
        ]) {
            if (from === undefined && to === undefined) {
                continue;
            }

            $scope.filterBy[key] = asRange(from, to);
            applied.push(label + " " + [from, to].filter(Boolean).join(" to "));
        }

        $scope.applyFilters();

        if (args.search !== undefined) {
            const term = String(args.search);

            $scope.certificationPeriodsGlobalFilter.val = term;
            $scope.applyCertificationPeriodsGlobalSearch(term);
            applied.push('"' + term + '"');
        }

        return applied.length === 0
            ? "Re-applied the certification period filters"
            : "Showing certification periods: " + applied.join(", ");
    });

    $scope.$on("$destroy", releaseCertificationPeriodsFilter);

    initialize();
};
